import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Mail, Lock, User, Check, X, Eye, EyeOff } from "lucide-react";
import storycraftLogo from "@/assets/storycraft-logo.png";
import whopLogo from "@/assets/whop-logo.png";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type AuthMode = "login" | "signup" | "forgot" | "reset";

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  // Check if we should start in signup mode (from checkout redirect)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("mode") === "signup") {
      setMode("signup");
    }
  }, [location.search]);

  useEffect(() => {
    // Check if this is a password reset callback
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get("type");
    
    if (type === "recovery") {
      setMode("reset");
    }
  }, []);

  // Check subscription/admin status and redirect appropriately
  const checkAccessAndRedirect = async (session: any) => {
    if (!session?.user || mode === "reset") return;

    try {
      // Check subscription status
      const { data, error } = await supabase.functions.invoke("check-subscription");
      
      if (error) {
        console.error("Error checking subscription:", error);
        // Default to checkout if check fails
        navigate("/checkout");
        return;
      }

      // Check if user is admin
      const { data: adminData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();

      const isAdmin = !!adminData;
      const hasActiveSubscription = data?.subscribed === true;

      // If admin or has active subscription, go to dashboard
      if (isAdmin || hasActiveSubscription) {
        const from = (location.state as any)?.from || "/app/dashboard";
        navigate(from);
      } else {
        // No subscription - go to checkout
        navigate("/checkout");
      }
    } catch (err) {
      console.error("Error during access check:", err);
      navigate("/checkout");
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setMode("reset");
        return;
      }
      
      if (session?.user && mode !== "reset") {
        checkAccessAndRedirect(session);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user && mode !== "reset") {
        checkAccessAndRedirect(session);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, mode, location.state]);

  const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");

    if (mode === "signup") {
      if (!isValidEmail(email.trim())) {
        setEmailError("Please enter a valid email address.");
        return;
      }
      if (email.trim().toLowerCase() !== confirmEmail.trim().toLowerCase()) {
        setEmailError("Email addresses do not match.");
        return;
      }
    }

    setIsLoading(true);

    try {
      if (mode === "login") {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          if (error.message.includes("Invalid login credentials")) {
            toast.error("Invalid email or password. Please try again.");
          } else {
            toast.error(error.message);
          }
        }
      } else if (mode === "signup") {
        if (password !== confirmPassword) {
          toast.error("Passwords do not match.");
          setIsLoading(false);
          return;
        }

        // Password validation: min 8 chars, 1 uppercase, 1 special character
        const hasUppercase = /[A-Z]/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        
        if (password.length < 8) {
          toast.error("Password must be at least 8 characters.");
          setIsLoading(false);
          return;
        }
        
        if (!hasUppercase) {
          toast.error("Password must contain at least one uppercase letter.");
          setIsLoading(false);
          return;
        }
        
        if (!hasSpecialChar) {
          toast.error("Password must contain at least one special character.");
          setIsLoading(false);
          return;
        }

        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/checkout`,
          },
        });

        if (error) {
          if (error.message.includes("already registered")) {
            toast.error("This email is already registered. Please sign in instead.");
            setMode("login");
          } else if (error.message.toLowerCase().includes("weak") || error.message.toLowerCase().includes("easy to guess")) {
            toast.error("Please choose a more unique password. Avoid common words like 'password', '123456', or your name.");
          } else {
            toast.error(error.message);
          }
        } else {
          toast.success("Account created! Redirecting to checkout...");
          // Will auto-redirect via onAuthStateChange
        }
      } else if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth`,
        });

        if (error) {
          toast.error(error.message);
        } else {
          toast.success("Password reset email sent! Check your inbox.");
          setMode("login");
        }
      } else if (mode === "reset") {
        if (password !== confirmPassword) {
          toast.error("Passwords do not match.");
          setIsLoading(false);
          return;
        }

        const { error } = await supabase.auth.updateUser({
          password: password,
        });

        if (error) {
          toast.error(error.message);
        } else {
          toast.success("Password updated successfully!");
          navigate("/app/dashboard");
        }
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case "login": return "Welcome back";
      case "signup": return "Start Your 7-Day Free Trial";
      case "forgot": return "Reset your password";
      case "reset": return "Set new password";
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case "login": return "Sign in to access your studio";
      case "signup": return "Create your account, then choose a plan. You won't be charged until your trial ends.";
      case "forgot": return "Enter your email to receive a reset link";
      case "reset": return "Choose a new password for your account";
    }
  };

  const getButtonText = () => {
    if (isLoading) return "Please wait...";
    switch (mode) {
      case "login": return "Sign In";
      case "signup": return "Continue to Plan Selection";
      case "forgot": return "Send Reset Link";
      case "reset": return "Update Password";
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <img src={storycraftLogo} alt="AI StoryCraft Studio" className="w-12 h-12 rounded-xl object-contain" />
            <span className="font-display text-2xl font-bold text-foreground">
              AI StoryCraft Studio
            </span>
          </div>

          {/* Header */}
          <h1 className="font-display text-3xl font-bold mb-2">{getTitle()}</h1>
          <p className="text-muted-foreground mb-8">{getSubtitle()}</p>

          {/* Mode Toggle - for login/signup */}
          {(mode === "login" || mode === "signup") && (
            <div className="flex gap-2 p-1 bg-muted rounded-xl mb-8">
              <button
                onClick={() => setMode("login")}
                className={cn(
                  "flex-1 py-2.5 rounded-lg font-medium transition-all text-sm",
                  mode === "login" 
                    ? "bg-card text-foreground shadow-sm" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Sign In
              </button>
              <button
                onClick={() => setMode("signup")}
                className={cn(
                  "flex-1 py-2.5 rounded-lg font-medium transition-all text-sm",
                  mode === "signup" 
                    ? "bg-card text-foreground shadow-sm" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Sign Up
              </button>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {mode !== "reset" && (
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                    className={`pl-10 h-12 bg-muted ${emailError ? "border-red-500" : "border-border"}`}
                    required
                  />
                </div>
                {mode === "signup" && (
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="confirmEmail"
                      type="email"
                      placeholder="Confirm your email"
                      value={confirmEmail}
                      onChange={(e) => { setConfirmEmail(e.target.value); setEmailError(""); }}
                      className={`pl-10 h-12 bg-muted ${emailError ? "border-red-500" : "border-border"}`}
                      required
                    />
                  </div>
                )}
                {emailError && <p className="text-sm text-red-500">{emailError}</p>}
              </div>
            )}


            {(mode === "login" || mode === "signup" || mode === "reset") && (
              <div className="space-y-2">
                <Label htmlFor="password">{mode === "reset" ? "New Password" : "Password"}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 bg-muted border-border"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {/* Password strength indicator */}
                {(mode === "signup" || mode === "reset") && password && (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center gap-2 text-xs">
                      {password.length >= 8 ? (
                        <Check className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <X className="w-3.5 h-3.5 text-red-500" />
                      )}
                      <span className={password.length >= 8 ? "text-green-500" : "text-muted-foreground"}>
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {/[A-Z]/.test(password) ? (
                        <Check className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <X className="w-3.5 h-3.5 text-red-500" />
                      )}
                      <span className={/[A-Z]/.test(password) ? "text-green-500" : "text-muted-foreground"}>
                        One uppercase letter
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {/[!@#$%^&*(),.?":{}|<>]/.test(password) ? (
                        <Check className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <X className="w-3.5 h-3.5 text-red-500" />
                      )}
                      <span className={/[!@#$%^&*(),.?":{}|<>]/.test(password) ? "text-green-500" : "text-muted-foreground"}>
                        One special character
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 italic">
                      Tip: Avoid common words like "password" or "123456"
                    </p>
                  </div>
                )}
              </div>
            )}

            {(mode === "signup" || mode === "reset") && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 bg-muted border-border"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            {mode === "login" && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="rememberMe" 
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked === true)}
                  />
                  <Label htmlFor="rememberMe" className="text-sm text-muted-foreground cursor-pointer">
                    Remember me
                  </Label>
                </div>
                <button
                  type="button"
                  onClick={() => setMode("forgot")}
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {mode === "forgot" && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-sm text-primary hover:underline"
                >
                  Back to sign in
                </button>
              </div>
            )}

            <Button type="submit" variant="hero" size="lg" className="w-full" disabled={isLoading}>
              {getButtonText()}
            </Button>
          </form>

          {/* Whop OAuth */}
          {(mode === "login" || mode === "signup") && (
            <div className="mt-6">
              <div className="relative flex items-center justify-center my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <span className="relative bg-background px-4 text-sm text-muted-foreground">or</span>
              </div>
              <Button
                type="button"
                size="lg"
                className="w-full bg-white text-black border border-border hover:bg-gray-50 font-semibold flex items-center justify-center gap-3"
                disabled={isLoading}
                onClick={() => {
                  const clientId = "app_g6j5BjH1rykWJP";
                  const redirectUri = encodeURIComponent(`${window.location.origin}/auth/whop/callback`);
                  window.location.href = `https://whop.com/oauth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=openid%20profile%20email`;
                }}
              >
                Sign in using Whop
                <img src={whopLogo} alt="Whop" className="h-8 w-auto" />
              </Button>
            </div>
          )}

          {/* Switch between login/signup */}
          {mode === "login" && (
            <div className="mt-6 text-center">
              <p className="text-muted-foreground">
                Don't have an account?{" "}
                <button onClick={() => setMode("signup")} className="text-primary hover:underline font-medium">
                  Sign Up
                </button>
              </p>
            </div>
          )}

          {mode === "signup" && (
            <div className="mt-6 text-center">
              <p className="text-muted-foreground">
                Already have an account?{" "}
                <button onClick={() => setMode("login")} className="text-primary hover:underline font-medium">
                  Sign In
                </button>
              </p>
            </div>
          )}

        </div>
      </div>

      {/* Right Panel - Decorative */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-neon-purple/20 via-background to-neon-yellow/10">
        <div className="absolute inset-0 bg-hero-glow" />
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-neon-purple/20 rounded-full blur-[80px]" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-neon-yellow/10 rounded-full blur-[80px]" />
        
        <div className="relative z-10 flex items-center justify-center w-full p-12">
          <div className="text-center">
            <h2 className="font-display text-4xl font-bold mb-4">
              {mode === "signup" ? (
                <>Join <span className="text-primary">4,200+ Creators</span></>
              ) : (
                <>Start Creating <span className="text-primary">Today</span></>

              )}
            </h2>
            <p className="text-muted-foreground text-lg max-w-md">
              {mode === "signup" 
                ? "Create your account and get instant access to all AI StoryCraft Studio tools."
                : "Join 230+ creators already using AI StoryCraft Studio to build profitable YouTube channels."
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;